import { AlertTriangle, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  useGovernanceOverride,
  type OverrideStatus,
} from '../hooks/useGovernanceOverride';

// Global Override Banner — sticky, full-width.
// 권위: ARCH_DASHBOARD.md v1.7 §4.0 / useGovernanceOverride 상태.
// 표시 규칙:
//   - rapidDrop / volatility 둘 다 비활성 → null (숨김).
//   - 하나라도 active → FATAL 수준 빨간 배너.
//   - 운영자가 [X] 로 닫으면 세션 로컬 dismissed — STOMP 재수신 시 되살아남.
//   - [이력 보기] → 화면 4 탭 6-D 딥링크 (/history?tab=governance-history).

function fmtTime(iso: string | null): string {
  if (!iso) return '—';
  // HH:mm 만 노출. 실패 시 원본 slice.
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(11, 16);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

function formatOverride(label: string, status: OverrideStatus): string {
  const t = fmtTime(status.triggeredAt);
  const reason = status.reason ?? 'reason 미기록';
  return `${label} (trig: ${t}, reason: ${reason})`;
}

export function GlobalOverrideBanner(): JSX.Element | null {
  const { state, dismiss, anyActive } = useGovernanceOverride();

  if (!anyActive) return null;
  if (state.dismissed) return null;

  const parts: string[] = [];
  if (state.rapidDrop.active) {
    parts.push(formatOverride('Rapid Drop', state.rapidDrop));
  }
  if (state.volatility.active) {
    parts.push(formatOverride('Volatility Expansion', state.volatility));
  }

  return (
    <div className="gov-override-banner" role="alert" aria-live="assertive">
      <AlertTriangle
        size={16}
        strokeWidth={2}
        className="gov-override-banner__icon"
        aria-hidden="true"
      />
      <span className="gov-override-banner__text">
        거버넌스 Override 발동 — {parts.join(' · ')}
      </span>
      <Link
        to="/history?tab=governance-history"
        className="gov-override-banner__link"
      >
        이력 보기
      </Link>
      <button
        type="button"
        className="gov-override-banner__close"
        aria-label="override 배너 닫기"
        onClick={dismiss}
      >
        <X size={14} strokeWidth={2} />
      </button>
    </div>
  );
}
